import { useState, useEffect } from 'react'

import { MultistepDialog, StepConfig } from '@/components/ui/multistep-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import {
  FileText,
  Loader2,
  UserPlus,
  UserCheck,
  Users,
  XCircle,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'
import {
  RelationshipAccessLevel,
  type ContactWithRelationships,
  IntegrationSource,
} from '@super/shared-types'
import { toast } from 'sonner'
import type {
  CSVProcessingResult,
  CSVPreviewResult,
} from '@/lib/csv-upload/contact-csv-processor'
import { ContactCSVProcessor } from '@/lib/csv-upload/contact-csv-processor'
import type { ContactUploadContext } from '@/types/csv-upload'
import { useContactResolution } from '@/hooks/useContactResolution'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  CSVFileUploader,
  PMSSelector,
  CSVPreviewTable,
  CSVColumnHeaders,
  CSVValidationSummary,
} from './shared'
import {
  getContactDisplayName,
  getContactSecondaryInfo,
  hasIdentifyingInfo,
} from '@/lib/utils/contact-display'

interface ContactCSVUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploadComplete?: (result: CSVProcessingResult) => void
  preloadedFile: File | null
  preselectedRelationship?: RelationshipAccessLevel | ''
  existingContacts?: ContactWithRelationships[]
  onFileChange: (file: File | null) => void
}

export function ContactCSVUploadDialog({
  open,
  onOpenChange,
  onUploadComplete,
  preloadedFile,
  preselectedRelationship,
  existingContacts = [],
  onFileChange,
}: ContactCSVUploadDialogProps) {
  const { currentCompany } = useAppAuth()
  const [currentStep, setCurrentStep] = useState(0)

  // Local state
  const [pmsType, setPMSType] = useState<IntegrationSource | ''>(
    IntegrationSource.APPFOLIO,
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const [, setError] = useState<string | null>(null)

  const [relationship, setRelationship] = useState<
    RelationshipAccessLevel | ''
  >(preselectedRelationship || '')
  const [previewResult, setPreviewResult] = useState<CSVPreviewResult | null>(
    null,
  )
  const [processingResult, setProcessingResult] =
    useState<CSVProcessingResult | null>(null)
  const [expandedSections, setExpandedSections] = useState({
    newContacts: false,
    duplicateCreates: false,
    updates: false,
    skipped: false,
  })

  // Contact resolution API
  const { executeContactResolution, isExecuting } = useContactResolution()

  // Update relationship when preselectedRelationship prop changes
  useEffect(() => {
    if (preselectedRelationship) {
      setRelationship(preselectedRelationship)
    }
  }, [preselectedRelationship])

  // Helper functions
  const handleCancel = () => {
    setPMSType(IntegrationSource.APPFOLIO)
    setIsProcessing(false)
    setError(null)
    onFileChange?.(null)
    setRelationship(preselectedRelationship || '')
    setPreviewResult(null)
    setProcessingResult(null)
    setExpandedSections({
      newContacts: false,
      duplicateCreates: false,
      updates: false,
      skipped: false,
    })
    setCurrentStep(0)
    onOpenChange(false)
  }

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleStepChange = async (step: number) => {
    if (step === 1 && !previewResult) {
      await processFile()
    } else if (step === 2 && !processingResult) {
      await processFile()
    }
    setCurrentStep(step)
  }

  const processFile = async () => {
    console.log('processFile called with:', {
      preloadedFile: !!preloadedFile,
      pmsType,
      relationship,
      currentCompany: !!currentCompany,
    })

    if (!preloadedFile || !pmsType || !relationship || !currentCompany) {
      console.log('Missing required configuration, returning early')
      setError('Missing required configuration')
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      const context: ContactUploadContext = {
        companyId: currentCompany.id,
        pmsProvider: pmsType as any,
        relationshipType: relationship,
      }

      console.log('Creating processor with context:', context)
      const processor = new ContactCSVProcessor(context)

      // First get preview result
      console.log('Parsing CSV...')
      const preview = await processor.parseCSV(preloadedFile)
      console.log('Preview result:', preview)
      setPreviewResult(preview)

      // Then process file for full results if preview is successful
      if (preview.success) {
        console.log('Processing file for resolution...')
        const result = await processor.processFile(
          preloadedFile,
          existingContacts,
        )
        console.log('Processing result:', result)
        setProcessingResult(result)
      }
    } catch (error) {
      console.error('processFile error:', error)
      setError(
        error instanceof Error ? error.message : 'Failed to process file',
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const handleComplete = async () => {
    if (!processingResult?.resolutionPlan) {
      setError('No resolution plan found')
      return
    }

    if (!pmsType) {
      setError('PMS type not selected')
      return
    }

    // Prevent double execution
    if (isExecuting) {
      return
    }

    try {
      toast.info('Executing contact resolution...', {
        description: 'Creating contacts and relationships in your CRM',
      })

      const result = await executeContactResolution(
        processingResult.resolutionPlan,
        currentCompany!.id,
        pmsType,
      )

      // Base the outcome on what actually landed in the CRM. Per-record failures
      // (e.g. a contact skipped for a bad phone) come back as errors alongside a
      // partial success — not a thrown request — so a bad row must not be shown
      // as a total failure (which would also skip the refresh and invite a
      // duplicate re-upload).
      const { createdPersons, updatedPersons } = result.results
      const errors = result.errors ?? []
      const landed = createdPersons > 0 || updatedPersons > 0

      // Nothing synced and errors reported → a genuine failure.
      if (!landed && errors.length > 0) {
        toast.error('Upload failed', {
          description:
            'No contacts could be synced to your CRM. Please try again.',
        })
        return
      }

      if (errors.length > 0) {
        // Errors are either contact-level (a record was skipped) or
        // relationship-level (the contact synced but a link didn't) — report
        // them distinctly so the message isn't misleading.
        const skippedContacts = errors.filter(
          e =>
            e.operation === 'create_person' || e.operation === 'update_person',
        ).length
        const relationshipIssues = errors.length - skippedContacts
        const detail = [
          `${createdPersons} created`,
          `${updatedPersons} updated`,
          skippedContacts > 0 ? `${skippedContacts} contact(s) skipped` : null,
          relationshipIssues > 0
            ? `${relationshipIssues} relationship issue(s)`
            : null,
        ]
          .filter(Boolean)
          .join(', ')
        toast.warning('Contacts synced with some issues', {
          description: `Synced ${detail}.`,
          duration: 6000,
        })
      } else {
        toast.success('Contact upload completed! 🎉', {
          description: `Successfully processed ${createdPersons + updatedPersons} contacts (${createdPersons} created, ${updatedPersons} updated)`,
          duration: 5000,
        })
      }

      // Refresh the contacts table and close, whether full or partial success.
      if (onUploadComplete && processingResult) {
        onUploadComplete(processingResult)
      }

      setTimeout(() => {
        handleCancel()
      }, 500)
    } catch (error) {
      console.error('Failed to execute contact resolution:', error)
      toast.error('Upload failed', {
        description: 'Failed to execute contact resolution. Please try again.',
      })
    }
  }

  // Step 1: Upload Configuration
  const step1CanProceed = Boolean(preloadedFile && pmsType && relationship)

  const step1Content = (
    <div className="space-y-4">
      <PMSSelector
        value={pmsType}
        onValueChange={setPMSType}
        disabled={isProcessing}
      />

      <div>
        <Label htmlFor="relationship">Relationship</Label>
        <Select
          value={relationship}
          onValueChange={(value: RelationshipAccessLevel) =>
            setRelationship(value)
          }
          disabled={isProcessing}
        >
          <SelectTrigger id="relationship" className="mt-2">
            <SelectValue placeholder="Select relationship" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={RelationshipAccessLevel.VENDOR}>
              Vendor
            </SelectItem>
            <SelectItem value={RelationshipAccessLevel.TENANT}>
              Tenant
            </SelectItem>
            <SelectItem value={RelationshipAccessLevel.OWNER}>Owner</SelectItem>
            <SelectItem value={RelationshipAccessLevel.MANAGER}>
              Property Manager
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <CSVFileUploader
        uploadedFile={preloadedFile}
        onFileChange={onFileChange}
        disabled={isProcessing}
      />
    </div>
  )

  // Step 2: Preview
  const step2Content = (
    <div className="space-y-4">
      {isProcessing ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Analyzing CSV file...</span>
        </div>
      ) : previewResult ? (
        <div className="space-y-4">
          <CSVValidationSummary
            success={previewResult.success}
            totalRows={previewResult.totalRows}
            processedRows={previewResult.processedRows}
            skipped={previewResult.skipped}
            errors={previewResult.errors}
            warnings={previewResult.warnings}
            entityType="contacts"
          />

          {previewResult.headers.length > 0 && (
            <CSVColumnHeaders
              headers={previewResult.headers}
              pmsType={pmsType}
              uploadType="contacts"
            />
          )}

          {previewResult.sampleRows.length > 0 && (
            <CSVPreviewTable
              headers={previewResult.headers}
              sampleRows={previewResult.sampleRows}
              pmsType={pmsType}
              uploadType="contacts"
            />
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <FileText className="h-6 w-6 mr-2" />
          <span>Preview will appear here</span>
        </div>
      )}
    </div>
  )

  // Step 3: Resolution Details
  const step3Content = (
    <div className="space-y-4 max-h-120 overflow-y-auto">
      {isProcessing ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Processing file...</span>
        </div>
      ) : processingResult?.resolutionPlan ? (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            {processingResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600" />
            )}
            <span className="font-medium">
              {processingResult.success
                ? 'Resolution plan ready'
                : 'Processing completed with issues'}
            </span>
          </div>

          {/* Summary Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
            <h4 className="font-medium text-sm text-gray-800">Summary</h4>
            <div className="flex justify-between text-sm text-gray-700">
              <span>Total rows processed:</span>
              <span className="font-medium">{processingResult.totalRows}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-700">
              <span>New contacts:</span>
              <span className="font-medium">
                {processingResult.resolutionPlan.summary.newContactsCount}
              </span>
            </div>
            <div className="flex justify-between text-sm text-gray-700">
              <span>Duplicates - Create:</span>
              <span className="font-medium">
                {processingResult.resolutionPlan.summary.duplicateCreatesCount}
              </span>
            </div>
            <div className="flex justify-between text-sm text-gray-700">
              <span>Duplicates - Update:</span>
              <span className="font-medium">
                {processingResult.resolutionPlan.summary.updatesCount}
                {processingResult.resolutionPlan.summary
                  .relationshipUpdatesCount > 0 &&
                  ` (${processingResult.resolutionPlan.summary.relationshipUpdatesCount} relationships)`}
              </span>
            </div>
            <div className="flex justify-between text-sm text-gray-700">
              <span>Skipped (no changes):</span>
              <span className="font-medium">
                {processingResult.resolutionPlan.summary.skippedCount}
              </span>
            </div>
          </div>

          {/* New Contacts Section */}
          {processingResult.resolutionPlan.actionGroups.newContacts.length >
            0 && (
            <div className="border rounded-lg">
              <button
                className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-50 rounded-md"
                onClick={() => toggleSection('newContacts')}
              >
                <div className="flex items-center space-x-2">
                  <UserPlus className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-sm text-gray-800">
                    New Contacts (
                    {
                      processingResult.resolutionPlan.actionGroups.newContacts
                        .length
                    }
                    )
                  </span>
                </div>
                {expandedSections.newContacts ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              {expandedSections.newContacts && (
                <div className="px-3 pb-3 space-y-2">
                  {processingResult.resolutionPlan.actionGroups.newContacts.map(
                    (action, index) => {
                      const hasIdInfo = hasIdentifyingInfo(action.finalContact)
                      const secondaryInfo = getContactSecondaryInfo(
                        action.finalContact,
                      )
                      return (
                        <div
                          key={index}
                          className={`p-3 rounded-md ${
                            hasIdInfo
                              ? 'bg-gray-50 border border-gray-200'
                              : 'bg-orange-50 border border-orange-300'
                          }`}
                        >
                          <div className="grid grid-cols-2 gap-4 items-center">
                            <div>
                              <div className="font-medium text-sm text-gray-900">
                                {getContactDisplayName(action.finalContact)}
                              </div>
                              {secondaryInfo && (
                                <div className="text-xs text-gray-600">
                                  {secondaryInfo}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 justify-end">
                              {action.relationships.length > 0 ? (
                                action.relationships.map((rel, relIndex) => (
                                  <span
                                    key={relIndex}
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                  >
                                    {rel.relationshipType}{' '}
                                    {rel.propertyExternalId
                                      ? `• ${rel.propertyExternalId}`
                                      : ''}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-500">
                                  No relationships
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    },
                  )}
                </div>
              )}
            </div>
          )}

          {processingResult.resolutionPlan.actionGroups.duplicateCreates
            .length > 0 && (
            <div className="border rounded-lg">
              <button
                className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-50 rounded-md"
                onClick={() => toggleSection('duplicateCreates')}
              >
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-sm text-gray-800">
                    Duplicates - Create (
                    {
                      processingResult.resolutionPlan.actionGroups
                        .duplicateCreates.length
                    }
                    )
                  </span>
                </div>
                {expandedSections.duplicateCreates ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              {expandedSections.duplicateCreates && (
                <div className="px-3 pb-3 space-y-2">
                  {processingResult.resolutionPlan.actionGroups.duplicateCreates.map(
                    (action, index) => {
                      const hasIdInfo = hasIdentifyingInfo(action.finalContact)
                      const secondaryInfo = getContactSecondaryInfo(
                        action.finalContact,
                      )
                      return (
                        <div
                          key={index}
                          className={`p-3 rounded-md ${
                            hasIdInfo
                              ? 'bg-gray-50 border border-gray-200'
                              : 'bg-orange-50 border border-orange-300'
                          }`}
                        >
                          <div className="grid grid-cols-2 gap-4 items-center">
                            <div>
                              <div className="font-medium text-sm text-gray-900">
                                {getContactDisplayName(action.finalContact)}
                              </div>
                              {secondaryInfo && (
                                <div className="text-xs text-gray-600">
                                  {secondaryInfo}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 justify-end">
                              {action.relationships?.length > 0 ? (
                                action.relationships.map((rel, relIndex) => (
                                  <span
                                    key={relIndex}
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                  >
                                    {rel.relationshipType}{' '}
                                    {rel.propertyExternalId
                                      ? `• ${rel.propertyExternalId}`
                                      : ''}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-500">
                                  No relationships
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    },
                  )}
                </div>
              )}
            </div>
          )}

          {/* Duplicate Updates Section */}
          {processingResult.resolutionPlan.actionGroups.updates.length > 0 && (
            <div className="border rounded-lg">
              <button
                className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-50 rounded-md"
                onClick={() => toggleSection('updates')}
              >
                <div className="flex items-center space-x-2">
                  <UserCheck className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-sm text-gray-800">
                    Duplicates - Update (
                    {
                      processingResult.resolutionPlan.actionGroups.updates
                        .length
                    }
                    )
                    {processingResult.resolutionPlan.actionGroups.updates.filter(
                      a => !a.hasFieldChanges && a.hasRelationshipChanges,
                    ).length > 0 &&
                      ` (${processingResult.resolutionPlan.actionGroups.updates.filter(a => !a.hasFieldChanges && a.hasRelationshipChanges).length} relationships only)`}
                  </span>
                </div>
                {expandedSections.updates ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              {expandedSections.updates && (
                <div className="px-3 pb-3 space-y-2">
                  {processingResult.resolutionPlan.actionGroups.updates.map(
                    (action, index) => {
                      const hasIdInfo = hasIdentifyingInfo(action.finalContact)
                      const secondaryInfo = getContactSecondaryInfo(
                        action.finalContact,
                      )
                      return (
                        <div
                          key={index}
                          className={`p-3 rounded-md ${
                            hasIdInfo
                              ? 'bg-gray-50 border border-gray-200'
                              : 'bg-orange-50 border border-orange-300'
                          }`}
                        >
                          <div className="grid grid-cols-2 gap-4 items-start">
                            <div>
                              <div className="font-medium text-sm text-gray-900">
                                {action.targetContactName}
                                {!action.hasFieldChanges &&
                                  action.hasRelationshipChanges && (
                                    <span className="ml-2 text-xs font-normal text-gray-600">
                                      (relationship only)
                                    </span>
                                  )}
                              </div>
                              {secondaryInfo && (
                                <div className="text-xs text-gray-600">
                                  {secondaryInfo}
                                </div>
                              )}
                              {action.fieldChanges.length > 0 && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {action.fieldChanges.length} field
                                  {action.fieldChanges.length === 1
                                    ? ''
                                    : 's'}{' '}
                                  updated
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 justify-end">
                              {action.relationships.length > 0 ? (
                                action.relationships.map((rel, relIndex) => (
                                  <span
                                    key={relIndex}
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                  >
                                    {rel.relationshipType}{' '}
                                    {rel.propertyExternalId
                                      ? `• ${rel.propertyExternalId}`
                                      : ''}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-500">
                                  No relationships
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    },
                  )}
                </div>
              )}
            </div>
          )}

          {processingResult.resolutionPlan.actionGroups.skipped.length > 0 && (
            <div className="border rounded-lg">
              <button
                className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-50 rounded-md"
                onClick={() => toggleSection('skipped')}
              >
                <div className="flex items-center space-x-2">
                  <XCircle className="h-4 w-4 text-gray-500" />
                  <span className="font-medium text-sm text-gray-800">
                    Skipped - No Changes (
                    {
                      processingResult.resolutionPlan.actionGroups.skipped
                        .length
                    }
                    )
                  </span>
                </div>
                {expandedSections.skipped ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              {expandedSections.skipped && (
                <div className="px-3 pb-3 space-y-2">
                  {processingResult.resolutionPlan.actionGroups.skipped.map(
                    (action, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 p-3 rounded-md"
                      >
                        <div className="font-medium text-sm mb-1 text-gray-900">
                          {action.targetContactName}
                        </div>
                        <div className="text-xs text-gray-600">
                          {action.reason}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          )}

          {processingResult.success && (
            <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 p-3 rounded-md">
              ✓ Review the resolution plan above and click "Complete" to apply
              all changes.
            </div>
          )}
        </div>
      ) : processingResult ? (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            {processingResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600" />
            )}
            <span className="font-medium">
              {processingResult.success
                ? 'Ready to import'
                : 'Processing completed with issues'}
            </span>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
            <div className="flex justify-between text-sm text-gray-700">
              <span>Total rows in CSV:</span>
              <span className="font-medium">{processingResult.totalRows}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-700">
              <span>Valid contacts to process:</span>
              <span className="font-medium">
                {processingResult.processedRows}
              </span>
            </div>
            <div className="flex justify-between text-sm text-gray-700">
              <span>Skipped (validation issues):</span>
              <span className="font-medium">
                {processingResult.warnings.length}
              </span>
            </div>
            {processingResult.errors.length > 0 && (
              <div className="flex justify-between text-sm text-gray-700">
                <span>Critical errors:</span>
                <span className="font-medium">
                  {processingResult.errors.length}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <span>Processing results will appear here</span>
        </div>
      )}
    </div>
  )

  const steps: StepConfig[] = [
    {
      id: 'configure',
      title: 'Upload contacts',
      content: step1Content,
      canProceed: step1CanProceed,
      nextText: 'Preview',
    },
    {
      id: 'preview',
      title: 'Preview data',
      content: step2Content,
      canProceed: previewResult?.success || false,
      nextText: 'Review',
    },
    {
      id: 'confirm',
      title: 'Review resolutions',
      content: step3Content,
      canProceed: (processingResult?.success || false) && !isExecuting,
      nextText: isExecuting ? 'Saving...' : 'Save',
    },
  ]

  return (
    <MultistepDialog
      open={open}
      onOpenChange={onOpenChange}
      steps={steps}
      currentStep={currentStep}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      onCancel={handleCancel}
      className="max-w-xl"
    />
  )
}
